import {
    useEffect,
    useMemo,
    useState,
} from 'react'

import Card from 'app/shared-components/Card'
import StatusHandler from 'app/shared-components/StatusHandler'
import OptionPanel
    from 'app/Apps/ContainerManagement/ServiceCenters/Monitoring/ContainerAvailabilityForecast/OptionPanel'
import ContainerAvailabilityForecastChart
    from 'app/Apps/ContainerManagement/ServiceCenters/Monitoring/ContainerAvailabilityForecast/ContainerAvailabilityForecastChart'
import strOrDateToDateStr from 'app/utils/date/strOrDateToDateStr'
import {
    ForecastAvailabilityOptions,
} from 'app/Apps/ContainerManagement/ServiceCenters/serviceCenters.types'
import {
    ContainerAvailabilityForecastContainerStatuses,
    ContainerAvailabilityForecastIncludeOptions,
} from 'app/enums/common.enums'
import {
    RequiredContainersCustomer,
} from 'app/types/common.types'

import useGetContainerAvailability from '../hooks/useGetContainerAvailability'
import RequiredContainersAvailability from '../RequiredContainersAvailability'
import useStyles from './containerAvailabilityForecast.styles'

const ContainerAvailabilityForecast = ({
    serviceCenterId,
    requiredContainers,
    orderNumber,
    requestedHandoverDate,
}: {
    serviceCenterId: number,
    requiredContainers: RequiredContainersCustomer[],
    orderNumber: string,
    requestedHandoverDate: string,
}) => {
    const {
        classes,
    } = useStyles()

    const [
        includesOrders,
        setIncludesOrders,
    ] = useState<Record<ContainerAvailabilityForecastIncludeOptions, boolean>>({
        [ContainerAvailabilityForecastIncludeOptions.IncludeNotStartedOrders]: false,
        [ContainerAvailabilityForecastIncludeOptions.IncludePreBookings]: false,
        [ContainerAvailabilityForecastIncludeOptions.IncludeAllDualContainers]: true,
    })

    const [
        optionChart,
        setOptionChart,
    ] = useState<ForecastAvailabilityOptions>({
        containerStatus: {
            [ContainerAvailabilityForecastContainerStatuses.ToBeProcessed]: true,
            [ContainerAvailabilityForecastContainerStatuses.InProcess]: true,
            [ContainerAvailabilityForecastContainerStatuses.Processed]: true,
            [ContainerAvailabilityForecastContainerStatuses.InPreconditioning]: true,
            [ContainerAvailabilityForecastContainerStatuses.Preconditioned]: true,
        },
        productType: undefined,
    })

    const {
        data: containerAvailability,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useGetContainerAvailability({
        serviceCenter: {
            id: serviceCenterId,
        },
        containersRequestedHandoverDate: requestedHandoverDate,
        requiredContainers,
        excludeOrderNumber: orderNumber,
        ...includesOrders,
    })

    useEffect(() => {
        if (!optionChart.productType && containerAvailability?.forecasts) {
            setOptionChart({
                ...optionChart,
                productType: containerAvailability?.forecasts?.[0]?.productType,
            })
        }
    }, [
        containerAvailability?.forecasts,
        optionChart,
    ])

    const allowedValuesProductType = useMemo(() => {
        return containerAvailability?.forecasts?.reduce((acc, {
            productType,
        }) => {
            return {
                ...acc,
                [productType]: productType,
            }
        }, {})
    }, [containerAvailability?.forecasts])

    const dataChart = useMemo(() => {
        return containerAvailability?.forecasts?.find(({
            productType,
        }) => {
            return productType === optionChart.productType
        })
    }, [
        containerAvailability?.forecasts,
        optionChart.productType,
    ])

    return (
        <>
            <Card
                data-testid="containersAvailability"
                className={classes.requiredContainers}
                title={`Container Availability As Of ${strOrDateToDateStr(requestedHandoverDate)}`}
            >
                <RequiredContainersAvailability
                    value={containerAvailability?.requiredContainersAvailability}
                />
            </Card>
            {Boolean(serviceCenterId) && (
                <Card
                    contentClass={classes.cardContent}
                    data-testid="availability-forecast-card"
                    title="Container Availability Forecast"
                >
                    <OptionPanel
                        value={optionChart}
                        onChange={setOptionChart}
                        allowedValues={allowedValuesProductType}
                        includesOrders={includesOrders}
                        setIncludesOrders={setIncludesOrders}
                    />
                    <StatusHandler
                        isSuccess={isDataReady && Boolean(dataChart)}
                        isFetching={isFetching}
                        isError={isError}
                        error={error}
                    >
                        <ContainerAvailabilityForecastChart
                            value={dataChart?.containerAvailabilityForecastForDates || []}
                            listStatuses={Object.keys(optionChart.containerStatus)}
                            containerStatus={optionChart.containerStatus}
                            minPreconditioned={dataChart?.minPreconditioned}
                        />
                    </StatusHandler>
                </Card>
            )}
        </>
    )
}

export default ContainerAvailabilityForecast
